import { ArrowBack, ArrowForward } from '@mui/icons-material'
import { Box, Container, Stack, Typography, useTheme, useMediaQuery } from '@mui/material'
import OtpInput from 'react-otp-input'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { ContainedButton, OutlinedButton } from '../../../components/Button'
import { useSnackbar } from '../../../hooks/snackbar/useSnackbar'
import { AppDispatch } from '../../../store'
import { VerificationActions, VerificationSelector } from '../../../store/Verification'

export const VerifyOtp = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { showSuccess, showError } = useSnackbar()
  const {
    verifyLoading,
    resendLoading,
    otp,
    inputEmail: email,
  } = useSelector(VerificationSelector.getVerificationState)

  const handleOtpChange = (value: string) => {
    dispatch(VerificationActions.setOtp(value))
  }

  const handleVerifyOtp = async () => {
    try {
      const resultAction: any = await dispatch(VerificationActions.verifyOtpAsync({ email, otp }))
      if (VerificationActions.verifyOtpAsync.fulfilled.match(resultAction)) {
        if (resultAction.payload.success) {
          showSuccess(resultAction.payload.message)
          navigate('/test-strip/upload')
          dispatch(VerificationActions.resetFormFields())
        }
      } else {
        showError(resultAction.payload.error)
      }
    } catch (err) {
      showError('Something went wrong. Please try again.')
    }
  }

  const handleResendOtp = async () => {
    try {
      const resultAction: any = await dispatch(VerificationActions.resendOtpAsync(email))
      if (VerificationActions.resendOtpAsync.fulfilled.match(resultAction)) {
        showSuccess(resultAction.payload.message || 'Verification code resent')
      } else {
        showError(resultAction?.payload?.error || 'Failed to resend code')
      }
    } catch (err) {
      showError('Something went wrong. Please try again.')
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        minHeight: 'calc(100vh - 68px)',
        py: 3,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Stack alignItems="center" spacing={3.5} sx={{ maxWidth: '450px' }}>
            <Stack alignItems="center">
              <Typography
                fontSize={{xs: "2rem", md:'2.2rem'}}
                fontWeight={theme.typography.fontWeightBold}
                color='text.primary'
                textAlign={'center'}
              >
                Check your email
              </Typography>

              <Typography fontSize={'1rem'} color={theme.palette.grey[600]} textAlign={'center'}>
                We've sent a verification code to{' '}
                <span style={{ fontWeight: 700, color: theme.palette.primary.dark }}>{email}</span>
              </Typography>
            </Stack>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              numInputs={6}
              renderInput={(props) => (
                <input
                  {...props}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={e => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    if (props.onChange) props.onChange(e);
                  }}
                />
              )}
              inputStyle={{
                margin: '4px',
                width: isMobile ? '45px' : '55.6px',
                height: isMobile ? '45px' : '56px',
                borderRadius: '8px',
                fontSize: '24px',
                backgroundColor: 'white',
                border: `1px solid ${theme.palette.divider}`,
                textAlign: 'center',
                outline: 'none',
              }}
              inputType="text"
              shouldAutoFocus
            />

            <Stack spacing={1} width="100%">
              <ContainedButton
                fullWidth
                size="large"
                sx={{ py: 2 }}
                disableElevation
                endIcon={<ArrowForward />}
                disabled={verifyLoading || !otp || otp.length !== 6}
                onClick={handleVerifyOtp}
              >
                {verifyLoading ? 'Verifying...' : 'Verify code'}
              </ContainedButton>
              <OutlinedButton
                fullWidth
                disabled={resendLoading}
                onClick={handleResendOtp}
                sx={{
                  border: 'none',
                  py: 1.5,
                }}
              >
                {resendLoading ? 'Resending...' : 'Resend code'}
              </OutlinedButton>

              <OutlinedButton
                fullWidth
                sx={{
                  border: 'none',
                  color: theme.palette.grey[600],
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: theme.palette.grey[500],
                  },
                }}
                onClick={() => {
                  dispatch(VerificationActions.clearVerification())
                  navigate('/test-strip/verify-email')
                }}
                startIcon={<ArrowBack fontSize="small" />}
              >
                change email address
              </OutlinedButton>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}
